import { useRef, useState, useEffect } from 'react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import ProfileCard from '@/components/ProfileCard';
import { useLanguage } from '@/LanguageContext';

const LOGOS = {
  aftereffects: (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#1a0038"/><path d="M97 164H69l-7 22H40l35-92h22l35 92h-22l-6-22zm-5-16l-9-31-9 31h18zM145 94h18v6c4-5 10-8 18-8 18 0 30 14 30 34s-12 34-30 34c-7 0-13-2-17-7v28h-19V94zm34 50c8 0 14-6 14-18s-6-18-14-18-14 6-14 18 6 18 14 18z" fill="#9999FF"/></svg>),
  premiere:     (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#0a0033"/><path d="M60 74h38c22 0 36 12 36 32s-14 32-36 32H80v28H60V74zm36 48c10 0 17-5 17-16s-7-16-17-16H80v32h16zM148 74h19v92h-19V74z" fill="#9999FF"/></svg>),
  photoshop:    (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#001933"/><path d="M60 74h38c22 0 36 12 36 32s-14 32-36 32H80v28H60V74zm36 48c10 0 17-5 17-16s-7-16-17-16H80v32h16zM148 140c0 10 8 16 18 16 6 0 11-2 11-7 0-13-38-5-38-33 0-15 13-24 30-24 18 0 30 10 31 26h-19c-1-8-5-12-13-12s-12 3-12 8c0 12 38 5 38 33 0 16-14 25-32 25-20 0-33-11-33-30h19v-2z" fill="#31A8FF"/></svg>),
  blender:      (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#0a0a0a"/><path d="M120 60c-33 0-60 27-60 60s27 60 60 60 60-27 60-60-27-60-60-60zm0 96c-19 0-36-17-36-36s17-36 36-36 36 17 36 36-17 36-36 36z" fill="#E87D0D"/><circle cx="120" cy="120" r="20" fill="#E87D0D"/><path d="M80 104H50" stroke="#E87D0D" strokeWidth="8" strokeLinecap="round"/></svg>),
  cinema4d:     (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#011a33"/><path d="M120 54c-36 0-66 30-66 66s30 66 66 66c20 0 38-9 50-23l-14-12c-9 10-21 17-36 17-26 0-47-21-47-48s21-48 47-48c14 0 26 6 35 15l14-13c-12-13-29-20-49-20z" fill="#0172CE"/><path d="M168 88h-22v28h-14v20h14v14h22v-14h14v-20h-14V88z" fill="#0172CE"/></svg>),
  davinci:      (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#0a0a0a"/><circle cx="120" cy="117" r="55" fill="none" stroke="#FF2D20" strokeWidth="8"/><circle cx="120" cy="117" r="30" fill="#FF2D20"/><circle cx="120" cy="117" r="12" fill="#0a0a0a"/></svg>),
  illustrator:  (<svg viewBox="0 0 240 234" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full"><rect width="240" height="234" rx="26" fill="#2d1a00"/><path d="M103 164H75l-7 22H46l35-92h22l35 92H116l-6-22zm-5-16l-9-31-9 31h18zM155 74h19v92h-19V74z" fill="#FF9A00"/></svg>),
};


const StarRating = ({ stars, color, size = 14 }) => (
  <div className="flex gap-1">
    {[1,2,3,4,5].map(i => (
      <motion.span key={i}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: i * 0.07, duration: 0.35, ease: [0.16,1,0.3,1] }}
        style={{ color: i <= stars ? color : 'rgba(255,255,255,0.1)', fontSize: size, lineHeight: 1, textShadow: i <= stars ? `0 0 8px ${color}` : 'none' }}
      >★</motion.span>
    ))}
  </div>
);

// ─── Detail Modal ─────────────────────────────────────────────────────────────
const DetailModal = ({ tool, onClose }) => {
  const logo = tool.logoUrl
    ? <img src={tool.logoUrl} alt={tool.name} className="w-full h-full object-contain" />
    : LOGOS[tool.id];

  return (
    <motion.div className="fixed inset-0 z-[9990] flex items-center justify-center p-4 md:p-8"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}>
      <motion.div className="absolute inset-0 backdrop-blur-xl"
        style={{ background: 'rgba(0,0,0,0.75)' }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />

      <motion.div className="relative z-10 w-full max-w-2xl overflow-hidden"
        style={{ background: '#0d0d0d', border: `1px solid ${tool.glowColor}30`, boxShadow: `0 0 80px ${tool.glowColor}20` }}
        initial={{ opacity: 0, y: 60, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.95 }}
        transition={{ duration: 0.5, ease: [0.16,1,0.3,1] }}
        onClick={e => e.stopPropagation()}>

        {/* Top glow bar */}
        <div className="h-[2px] w-full" style={{ background: `linear-gradient(to right, transparent, ${tool.glowColor}, transparent)` }} />

        {/* Hero area */}
        <div className="relative p-8 pb-0 overflow-hidden">
          {/* Blurred bg logo */}
          <div className="absolute inset-0 flex items-center justify-center opacity-5 scale-150 blur-2xl pointer-events-none">
            <div className="w-64 h-64">{logo}</div>
          </div>
          {/* Radial glow */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: `radial-gradient(ellipse 60% 60% at 20% 50%, ${tool.glowColor}12, transparent)` }} />

          <div className="relative flex items-start gap-6 mb-8">
            {/* Logo */}
            <motion.div className="w-20 h-20 flex-shrink-0"
              initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.5, ease: [0.16,1,0.3,1] }}>
              {logo}
            </motion.div>
            {/* Title */}
            <div className="flex-1">
              <motion.p className="font-mono text-[9px] uppercase tracking-[0.4em] mb-1"
                style={{ color: tool.glowColor }}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                {tool.category}
              </motion.p>
              <motion.h3 className="font-mono text-2xl md:text-3xl uppercase tracking-tight text-black dark:text-white mb-3"
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                {tool.name}
              </motion.h3>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                <StarRating stars={tool.stars} color={tool.glowColor} size={18} />
              </motion.div>
            </div>
            {/* Close */}
            <button onClick={onClose}
              className="font-mono text-[10px] uppercase tracking-widest text-black/40 dark:text-white/20 hover:text-black dark:text-white transition-colors px-3 py-1 border border-black/20 dark:border-white/10 hover:border-black/40 dark:border-white/30">
              ✕
            </button>
          </div>

          {/* Description */}
          <motion.p className="font-mono text-sm text-black/70 dark:text-white/50 leading-relaxed relative"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            {tool.description}
          </motion.p>
        </div>

        {/* Details */}
        <div className="p-8 pt-6 space-y-0">
          {tool.details.map((d, i) => (
            <motion.div key={d.label}
              className="flex items-start justify-between py-4 border-t border-black/10 dark:border-white/5"
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.35 + i * 0.07, duration: 0.4, ease: [0.16,1,0.3,1] }}>
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-black/40 dark:text-white/25 mt-0.5 flex-shrink-0 w-32">{d.label}</span>
              <span className="font-mono text-sm text-black/90 dark:text-white/70 text-right">{d.value}</span>
            </motion.div>
          ))}
        </div>

        {/* Tags */}
        <motion.div className="px-8 pb-8 flex flex-wrap gap-2"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}>
          {tool.tags.map((tag, i) => (
            <motion.span key={tag}
              className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border"
              style={{ borderColor: `${tool.glowColor}30`, color: `${tool.glowColor}bb` }}
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.55 + i * 0.05 }}>
              {tag}
            </motion.span>
          ))}
        </motion.div>

        {/* Bottom accent */}
        <div className="h-px w-full" style={{ background: `linear-gradient(to right, transparent, ${tool.glowColor}40, transparent)` }} />
      </motion.div>
    </motion.div>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────
export const AwardsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);
  const [selected, setSelected] = useState(null);
  const [tools, setTools] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
    fetch(`${API}/tools`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setTools(data); })
      .catch(() => {});
  }, []);

  return (
    <section id="awards" ref={ref} data-testid="awards-section"
      className="relative py-32 md:py-40 px-6 md:px-12 lg:px-24 overflow-hidden">
      <motion.div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505]" style={{ y: bgY }} />

      <div className="max-w-screen-2xl mx-auto relative">
        <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <motion.span className="inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-black/50 dark:text-white/30 border border-black/20 dark:border-white/10 px-4 py-2 mb-6"
              initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}}>
              {t('awards', 'subtitle')}
            </motion.span>
            <div className="overflow-hidden">
              <motion.h2 className="font-mono text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-black dark:text-white"
                initial={{ y: '100%' }} animate={isInView ? { y: 0 } : {}}
                transition={{ duration: 0.8, ease: [0.16,1,0.3,1] }}>
                {t('awards', 'title')}  
              </motion.h2>
            </div>
          </div>
          <motion.p className="font-mono text-xs text-black/50 dark:text-white/30 max-w-xs leading-relaxed"
            initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.3 }}>
            <span style={{
            background: 'linear-gradient(90deg, #E2FF00, #00f0ff, #bc13fe)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% auto',
            animation: 'gradientShift 3s linear infinite',
            filter: 'drop-shadow(0 0 10px rgba(226,255,0,0.5)) drop-shadow(0 0 20px rgba(0,240,255,0.3))',
          }}>
            {t('awards', 'desc')}
          </span>
          </motion.p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 md:gap-5">
          {tools.map((tool, i) => (
            <motion.div key={tool.id || i}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.07, duration: 0.75, ease: [0.16,1,0.3,1] }}
              onClick={() => setSelected(tool)}
              style={{ cursor: 'pointer' }}>
              <ProfileCard
                name={tool.name}
                title={tool.title}
                stars={tool.stars}
                starColor={tool.starColor}
                avatarUrl={tool.logoUrl}
                glowColor={tool.glowColor}
                innerGradient={tool.innerGradient}
                cardHeight={tool.cardHeight}
                cardWidth={tool.cardWidth}
              />
            </motion.div>
          ))}
        </div>

        <div className="mt-24 overflow-hidden">
          <motion.div className="marquee" initial={{ opacity: 0 }} animate={isInView ? { opacity: 1 } : {}} transition={{ delay: 0.8 }}>
            <div className="marquee-content">
              {[...Array(4)].map((_, i) => (<span key={i} className="text-7xl md:text-9xl font-display font-bold uppercase text-white/[0.02] whitespace-nowrap mx-8">{t('awards', 'bg_text1', 'KNOXOPIA DIGITAL •')}</span>))}
            </div>
            <div className="marquee-content" aria-hidden="true">
              {[...Array(4)].map((_, i) => (<span key={i} className="text-7xl md:text-9xl font-display font-bold uppercase text-white/[0.02] whitespace-nowrap mx-8">{t('awards', 'bg_text2', 'KNOXOPIA ART •')}</span>))}
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {selected && <DetailModal tool={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
};

export default AwardsSection;